import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Skeleton,
  Menu as MuiMenu,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchEmailTemplateMeta,
  fetchEmailTemplates,
  deleteEmailTemplate,
  duplicateEmailTemplate,
  setEmailTemplateActive,
  CommunicationTemplateType,
  IEmailTemplate,
  IEmailTemplateMeta,
  EmailTemplateStatusFilter,
} from "../../../Api/emailTemplatesApi";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm } from "../../../Components/Confirm";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../../Course/shared/moduleSx";
import EditorDrawer from "./EditorDrawer";
import PreviewDialog from "./PreviewDialog";

type TabValue = "all" | "email" | "sms";

// Both channels have their own icon so the list scans quickly.
const CHANNEL_META: Record<
  CommunicationTemplateType,
  { label: string; icon: React.ReactNode; tone: string }
> = {
  email:           { label: "Email",          icon: <EmailOutlinedIcon fontSize="small" />, tone: "#1F6FEB" },
  sms:             { label: "SMS",            icon: <SmsOutlinedIcon fontSize="small" />,   tone: "#22c55e" },
  customer_message:{ label: "Message",        icon: <ForumOutlinedIcon fontSize="small" />, tone: "#f59e0b" },
  call_log:        { label: "Call Log",       icon: <ForumOutlinedIcon fontSize="small" />, tone: "#8b5cf6" },
  internal_note:   { label: "Internal Note",  icon: <ForumOutlinedIcon fontSize="small" />, tone: "#64748b" },
  meeting_note:    { label: "Meeting Note",   icon: <ForumOutlinedIcon fontSize="small" />, tone: "#0ea5e9" },
  follow_up:       { label: "Follow-up",      icon: <ForumOutlinedIcon fontSize="small" />, tone: "#ec4899" },
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: "10px",
    bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
  "& .MuiInputLabel-root": { fontWeight: 500, color: B13Ink.ink3 },
  "& .MuiInputLabel-root.Mui-focused": { color: B13.primary },
} as const;

const CommunicationTemplatesPage: React.FC = () => {
  const { showErrorMessage, showMessage } = useNotifier();
  const confirm = useConfirm();

  // ── data ─────────────────────────────────────────────────────────
  const [meta, setMeta] = useState<IEmailTemplateMeta | null>(null);
  const [rows, setRows] = useState<IEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // ── filters / tabs ───────────────────────────────────────────────
  const [tab, setTab] = useState<TabValue>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState<string>("");
  const [status, setStatus] = useState<EmailTemplateStatusFilter>("all");

  // ── row menu ─────────────────────────────────────────────────────
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; row: IEmailTemplate } | null>(null);

  // ── editor / preview ─────────────────────────────────────────────
  const [editor, setEditor] = useState<
    | { mode: "create"; channel: CommunicationTemplateType }
    | { mode: "edit"; row: IEmailTemplate }
    | null
  >(null);
  const [preview, setPreview] = useState<IEmailTemplate | null>(null);

  // Debounce search — the list endpoint is server-filtered so we avoid
  // spamming it on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchEmailTemplates({
        search: debouncedSearch || undefined,
        category: category || undefined,
        status: status === "all" ? undefined : status,
        template_type: tab === "all" ? undefined : (tab as CommunicationTemplateType),
      });
      setRows(list);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, category, status, tab, showErrorMessage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchEmailTemplateMeta()
      .then(setMeta)
      .catch((e) => showErrorMessage(e?.message || "Failed to load template metadata."));
  }, [showErrorMessage]);

  // Category options adapt to the selected channel tab so we never
  // show, say, a "Marketing" bucket when the user is filtering to SMS
  // and there are no SMS marketing templates.
  const categoryOptions = useMemo(() => {
    if (!meta) return [] as { value: string; label: string }[];
    if (tab === "all") return meta.categories;
    return meta.categories_by_type[tab as CommunicationTemplateType] ?? [];
  }, [meta, tab]);

  const counts = useMemo(() => {
    return {
      all:   rows.length,
      email: rows.filter((r) => r.template_type === "email").length,
      sms:   rows.filter((r) => r.template_type === "sms").length,
    };
  }, [rows]);

  const handleDuplicate = async (row: IEmailTemplate) => {
    setRowMenu(null);
    try {
      const created = await duplicateEmailTemplate(row.id);
      showMessage(`Duplicated "${created.name}".`);
      load();
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to duplicate template.");
    }
  };

  const handleToggleActive = async (row: IEmailTemplate) => {
    setRowMenu(null);
    try {
      await setEmailTemplateActive(row.id, !row.is_active);
      showMessage(row.is_active ? "Template archived." : "Template activated.");
      load();
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to update template.");
    }
  };

  const handleDelete = async (row: IEmailTemplate) => {
    setRowMenu(null);
    if (row.is_default) {
      showErrorMessage(
        "System templates can't be deleted. Archive it instead if you no longer need it."
      );
      return;
    }
    const ok = await confirm({
      title: "Delete template?",
      message: `Delete "${row.name}"? This can't be undone.`,
      confirmText: "Delete",
      variant: "danger",
      onConfirm: async () => { await deleteEmailTemplate(row.id); },
      successMessage: "Template deleted.",
    });
    if (ok) load();
    // Errors surfaced by ConfirmProvider's own toast; caller-side
    // showErrorMessage isn't needed when onConfirm is used.
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      {/* ── PAGE HEADER ─────────────────────────────────────────────── */}
      <Box sx={{ ...surfaceSx, p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box
            sx={{
              width: 48, height: 48, borderRadius: 2.5, display: "flex",
              alignItems: "center", justifyContent: "center",
              bgcolor: tint12(B13.primary), color: B13.primary, flexShrink: 0,
              "& svg": { fontSize: 24 },
            }}
          >
            <EmailOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
              Communication Templates
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.25, maxWidth: 720 }}>
              Create reusable Email and SMS templates for staff, parents, students, administrators
              and campaigns.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditor({ mode: "create", channel: tab === "sms" ? "sms" : "email" })}
            sx={{
              textTransform: "none",
              bgcolor: B13.primary,
              "&:hover": { bgcolor: B13.primary },
              borderRadius: "10px",
              height: 40,
              px: 2,
              fontWeight: 600,
            }}
          >
            New Template
          </Button>
        </Stack>
      </Box>

      {/* ── TABS + SEARCH + FILTERS ─────────────────────────────────── */}
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={0.5} sx={{ bgcolor: "#F1F5F9", borderRadius: "10px", p: 0.5 }}>
            {(["all", "email", "sms"] as TabValue[]).map((t) => {
              const active = t === tab;
              const label = t === "all" ? "All" : t === "email" ? "Email" : "SMS";
              const count = counts[t];
              return (
                <Box
                  key={t}
                  component="button"
                  type="button"
                  onClick={() => setTab(t)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.75,
                    px: 1.5, height: 32, borderRadius: "8px", border: "none",
                    cursor: "pointer", font: "inherit", fontWeight: 600, fontSize: 13,
                    bgcolor: active ? "#FFFFFF" : "transparent",
                    color: active ? B13.primary : B13Ink.ink2,
                    boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
                    transition: "background-color 120ms ease",
                  }}
                >
                  <span>{label}</span>
                  {tab === "all" || active ? (
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 18, fontSize: 11, fontWeight: 700,
                        bgcolor: active ? tint12(B13.primary) : "#E2E8F0",
                        color: active ? B13.primary : B13Ink.ink2,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Stack>

          <TextField
            size="small"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...filterFieldSx, flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <TextField
            select
            size="small"
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ ...filterFieldSx, minWidth: 180 }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categoryOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as EmailTemplateStatusFilter)}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Archived</MenuItem>
          </TextField>
        </Stack>
      </Box>

      {/* ── LIST ───────────────────────────────────────────────────── */}
      <Box sx={{ ...surfaceSx, borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Stack spacing={1.25} sx={{ p: 2 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: "12px" }} />
            ))}
          </Stack>
        ) : rows.length === 0 ? (
          <Box sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
            <Box
              sx={{
                width: 56, height: 56, borderRadius: 3, mx: "auto", mb: 1.5,
                bgcolor: tint12(B13.primary), color: B13.primary,
                display: "flex", alignItems: "center", justifyContent: "center",
                "& svg": { fontSize: 28 },
              }}
            >
              <EmailOutlinedIcon />
            </Box>
            <Typography sx={{ ...B13TypeScale.h3, mb: 0.5 }}>No templates yet</Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mb: 2 }}>
              Save a reusable message once, then reuse it across the app.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEditor({ mode: "create", channel: tab === "sms" ? "sms" : "email" })}
              sx={{
                textTransform: "none", bgcolor: B13.primary,
                "&:hover": { bgcolor: B13.primary }, borderRadius: "10px",
              }}
            >
              New Template
            </Button>
          </Box>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${B13.line}` }} />}>
            {rows.map((row) => {
              const channel = CHANNEL_META[row.template_type] ?? CHANNEL_META.email;
              return (
                <Box
                  key={row.id}
                  sx={{
                    px: { xs: 1.5, md: 2 }, py: 1.5,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr auto", md: "1fr 140px 160px 120px auto" },
                    gap: { xs: 1, md: 2 },
                    alignItems: "center",
                    transition: "background-color 120ms ease",
                    "&:hover": { bgcolor: "#F8FAFC" },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: `${channel.tone}1A`, color: channel.tone,
                      }}
                    >
                      {channel.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14, fontWeight: 700, color: B13.ink,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}
                        >
                          {row.name}
                        </Typography>
                        {row.is_default && (
                          <Tooltip title="System template — can't be deleted">
                            <LockOutlinedIcon sx={{ fontSize: 14, color: B13Ink.ink3 }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: 12, color: B13Ink.ink2, mt: 0.25,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}
                      >
                        {row.template_type === "email" && row.subject ? row.subject : row.body_text || row.description || "—"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Chip
                      size="small"
                      label={row.template_type_label || channel.label}
                      icon={channel.icon as React.ReactElement}
                      sx={{
                        height: 24, fontSize: 11.5, fontWeight: 600,
                        bgcolor: `${channel.tone}14`, color: channel.tone,
                        "& .MuiChip-icon": { color: channel.tone, ml: 0.5 },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2 }} noWrap>
                      {row.category_label || row.category || "—"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Chip
                      size="small"
                      label={row.is_active ? "Active" : "Archived"}
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 700,
                        bgcolor: row.is_active ? "#DCFCE7" : "#F1F5F9",
                        color: row.is_active ? "#15803D" : "#64748B",
                      }}
                    />
                  </Box>

                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Preview">
                      <IconButton size="small" onClick={() => setPreview(row)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditor({ mode: "edit", row })}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More">
                      <IconButton
                        size="small"
                        onClick={(e) => setRowMenu({ anchor: e.currentTarget, row })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* ── ROW MENU ───────────────────────────────────────────────── */}
      <MuiMenu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem onClick={() => rowMenu && handleDuplicate(rowMenu.row)}>
          <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => rowMenu && handleToggleActive(rowMenu.row)}>
          {rowMenu?.row.is_active ? "Archive" : "Activate"}
        </MenuItem>
        <MenuItem
          onClick={() => rowMenu && handleDelete(rowMenu.row)}
          disabled={rowMenu?.row.is_default}
          sx={{ color: "#dc2626" }}
        >
          <DeleteOutlineOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </MuiMenu>

      {/* ── EDITOR ─────────────────────────────────────────────────── */}
      {editor && meta && (
        <EditorDrawer
          open
          meta={meta}
          initialRow={editor.mode === "edit" ? editor.row : null}
          initialChannel={editor.mode === "create" ? editor.channel : editor.row.template_type}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}

      {/* ── PREVIEW ───────────────────────────────────────────────── */}
      {preview && (
        <PreviewDialog
          template={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </Box>
  );
};

export default CommunicationTemplatesPage;
